import React, {useEffect, useRef, useState} from "react";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import {get, post} from "../../utils/http.client";
import {apiBindings} from "../../utils/constants";
import "./secret.view.css";

type Mode = 'login' | 'register' | 'forgot';
type NameStatus = 'none' | 'checking' | 'available' | 'unavailable';

// Editor register accounts are created in the debug title (see Register.cs), so
// the availability check queries that environment's profile table.
const REGISTER_ENV = 'debug';

// WHATWG HTML "valid email address" pattern — the same one <input type="email">
// uses. A deliberate, pragmatic simplification of RFC 5322 (full RFC is
// impractical to regex): accepts effectively every real-world address while
// rejecting obvious garbage.
//
// Character-for-character the spec's pattern except for one thing: the spec
// writes the forward slash in the local-part class as `\/`, which inside a
// character class escapes nothing (a bare `/` can't end the literal there) and
// trips no-useless-escape. Left unescaped here — same language matched.
const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

function SecretView() {
    const {refreshBranches} = useIntegrator();
    const {setLoading, checkSession, login, register} = useApp();

    const [mode, setMode] = useState<Mode>('login');
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirm, setConfirm] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [nameStatus, setNameStatus] = useState<NameStatus>('none');
    // Errors surface after the first submit attempt, then track the fields live.
    const [submitted, setSubmitted] = useState<boolean>(false);
    // True once a password-reset email has been requested (shows the confirmation).
    const [forgotSent, setForgotSent] = useState<boolean>(false);

    const initialized = useRef(false);
    // Debounce + stale-guard for the name availability check (mirrors the client).
    const nameTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const nameToken = useRef(0);

    const isRegister = mode === 'register';
    const isForgot = mode === 'forgot';
    const isLogin = mode === 'login';

    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        async function restoreSession() {
            setLoading(true);
            await refreshBranches();
            await checkSession();
            setLoading(false);
        }

        restoreSession();
    }, [setLoading, refreshBranches, checkSession]);

    // Cancel any pending name check on unmount.
    useEffect(() => () => {
        if (nameTimer.current) clearTimeout(nameTimer.current);
    }, []);

    function cancelNameCheck() {
        nameToken.current++;
        if (nameTimer.current) clearTimeout(nameTimer.current);
    }

    function onNameChange(value: string) {
        setName(value);
        cancelNameCheck(); // invalidate the last verdict + any in-flight check

        const trimmed = value.trim();
        if (trimmed.length < 3 || trimmed.length > 20 || !/^[a-zA-Z0-9]+$/.test(trimmed)) {
            setNameStatus('none');
            return;
        }

        setNameStatus('checking');
        const token = nameToken.current;
        nameTimer.current = setTimeout(() => checkName(trimmed, token), 500);
    }

    async function checkName(value: string, token: number) {
        const url = `${apiBindings.api}checkDisplayName?name=${encodeURIComponent(value)}&env=${REGISTER_ENV}`;
        const result = await get(url, undefined);
        if (token !== nameToken.current) return; // stale — the name changed meanwhile
        if (!result.success) {
            // Unknown (server/network) — don't assert; PlayFab still enforces uniqueness.
            setNameStatus('none');
            return;
        }
        setNameStatus(result.data?.available ? 'available' : 'unavailable');
    }

    // Keep the inline error in sync with the fields. Password-mismatch shows
    // eagerly as you type; every other validation message appears only after a
    // submit attempt, then clears (or advances to the next issue) as you fix
    // things. Runs as an effect so it always sees the latest field values.
    useEffect(() => {
        // Preemptive email-format feedback: once the field has content, flag a
        // malformed address and clear it the moment it becomes valid.
        if (email.length > 0 && !EMAIL_RE.test(email)) {
            setError('Enter a valid email address.');
            return;
        }
        if (isRegister && confirm.length > 0 && password !== confirm) {
            setError('Passwords must match');
            return;
        }
        if (!submitted) {
            setError(undefined);
            return;
        }
        if (isForgot) {
            setError(email ? undefined : 'Email is required.');
            return;
        }
        const validationError = validate();
        if (validationError) {
            setError(validationError);
        } else if (isRegister && nameStatus === 'unavailable') {
            setError('That name is already taken');
        } else {
            setError(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, email, password, confirm, mode, submitted, nameStatus]);

    function validate(): string | undefined {
        if (isRegister) {
            const n = name.trim();
            if (n.length < 3 || n.length > 20) return 'Name must be 3–20 characters.';
            if (!/^[a-zA-Z0-9]+$/.test(n)) return 'Name must be letters and numbers only.';
        }
        if (!email) return 'Email is required.';
        if (!EMAIL_RE.test(email)) return 'Enter a valid email address.';
        if (!password) return 'Password is required.';
        if (isRegister) {
            if (password.length < 6) return 'Password must be at least 6 characters.';
            if (password !== confirm) return 'Passwords must match';
        }
        return undefined;
    }

    async function submit() {
        // Surface errors from here on; the effect above renders/clears them.
        setSubmitted(true);
        if (validate()) return;
        if (isRegister && nameStatus === 'unavailable') return;
        setLoading(true);
        if (isRegister) await register(name, email, password);
        else await login(email, password);
        setLoading(false);
    }

    // Triggers a PlayFab account-recovery email via the backend. Always shows the
    // same confirmation (the backend never reveals whether the email is registered).
    async function forgotSubmit() {
        setSubmitted(true);
        if (!email || !EMAIL_RE.test(email)) return; // the effect renders the reason
        setLoading(true);
        await post(`${apiBindings.api}forgotPassword`, JSON.stringify({email}), undefined);
        setLoading(false);
        setForgotSent(true);
    }

    function onKeyDown(e: React.KeyboardEvent) {
        if (e.key !== 'Enter') return;
        if (isForgot) forgotSubmit();
        else submit();
    }

    function switchMode(next: Mode) {
        setMode(next);
        setError(undefined);
        setNameStatus('none');
        setSubmitted(false);
        setForgotSent(false);
        cancelNameCheck();
    }

    function title(): string {
        if (isRegister) return 'Create your Rift Account';
        if (isForgot) return 'Reset your password';
        return 'Welcome to Rift';
    }

    function nameIndicator() {
        switch (nameStatus) {
            case 'checking':
                return <span className={'fa-solid fa-circle-notch fa-spin auth-status-neutral'}/>;
            case 'available':
                return <span className={'fa-solid fa-check auth-status-ok'}/>;
            case 'unavailable':
                return <span className={'fa-solid fa-xmark auth-status-bad'}/>;
            default:
                return null;
        }
    }

    return (
        <div className={'auth-screen'}>
            <div className={'auth-card'}>
                <p className={'auth-title'}>{title()}</p>

                {isForgot && forgotSent
                    ? <>
                        <p className={'auth-notice'}>
                            If an account exists for that email, a reset link is on its way.
                        </p>
                        <button type={'button'} className={'auth-primary'} onClick={() => switchMode('login')}>
                            Back to sign in
                        </button>
                    </>
                    : <>
                        {isRegister &&
                            <div className={'auth-row'}>
                                <span className={'auth-label'}>Name</span>
                                <div className={'auth-control'}>
                                    <input className={'auth-input'} type={'text'} placeholder={'Player name'} value={name}
                                           autoFocus onChange={e => onNameChange(e.target.value)} onKeyDown={onKeyDown}/>
                                    <span className={'auth-trailing'}>{nameIndicator()}</span>
                                </div>
                            </div>}

                        <div className={'auth-row'}>
                            <span className={'auth-label'}>Email</span>
                            <div className={'auth-control'}>
                                <input className={'auth-input'} type={'email'} placeholder={'user@email.com'} value={email}
                                       autoFocus={!isRegister} onChange={e => setEmail(e.target.value)} onKeyDown={onKeyDown}/>
                            </div>
                        </div>

                        {!isForgot &&
                            <div className={'auth-row'}>
                                <span className={'auth-label'}>Password</span>
                                <div className={'auth-control'}>
                                    <input className={'auth-input'} type={showPassword ? 'text' : 'password'} placeholder={'Password'}
                                           value={password} onChange={e => setPassword(e.target.value)} onKeyDown={onKeyDown}/>
                                    <span className={'auth-trailing'}>
                                        <button type={'button'} className={'auth-eye'} onClick={() => setShowPassword(s => !s)}>
                                            <span className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}/>
                                        </button>
                                    </span>
                                </div>
                            </div>}

                        {isRegister &&
                            <div className={'auth-row'}>
                                <span className={'auth-label'}>Password verify</span>
                                <div className={'auth-control'}>
                                    <input className={'auth-input'} type={showConfirm ? 'text' : 'password'} placeholder={'Password'}
                                           value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={onKeyDown}/>
                                    <span className={'auth-trailing'}>
                                        <button type={'button'} className={'auth-eye'} onClick={() => setShowConfirm(s => !s)}>
                                            <span className={`fa-solid ${showConfirm ? 'fa-eye-slash' : 'fa-eye'}`}/>
                                        </button>
                                    </span>
                                </div>
                            </div>}

                        <p className={'auth-error'}>{error ?? ''}</p>

                        {isRegister &&
                            <div className={'auth-actions'}>
                                <button type={'button'} onClick={() => switchMode('login')}>Cancel</button>
                                <button type={'button'} className={'auth-primary'} onClick={submit}>Register</button>
                            </div>}

                        {isForgot &&
                            <>
                                <button type={'button'} className={'auth-primary'} onClick={forgotSubmit}>Send reset email</button>
                                <button type={'button'} className={'auth-switch'} onClick={() => switchMode('login')}>
                                    Back to sign in
                                </button>
                            </>}

                        {isLogin &&
                            <>
                                <button type={'button'} className={'auth-primary'} onClick={submit}>Sign in</button>
                                <button type={'button'} className={'auth-switch'} onClick={() => switchMode('register')}>
                                    Create an account
                                </button>
                                <button type={'button'} className={'auth-switch'} onClick={() => switchMode('forgot')}>
                                    Forgot password?
                                </button>
                            </>}
                    </>}
            </div>
        </div>
    );
}

export default SecretView;
